<template>
  <div class="chat-view min-h-screen bg-gray-900 text-white">
    <div class="container mx-auto py-6 px-4">
      <h1 class="text-2xl font-bold mb-6">Poruke</h1>

      <div v-if="authError" class="bg-red-800 p-4 rounded-lg mb-6">
        <p class="text-white">
          Problem sa autentifikacijom. Molimo
          <a href="/login" class="underline">prijavite se</a> ponovo.
        </p>
      </div>

      <div
        v-else-if="showMainLoader"
        class="flex justify-center items-center h-96"
      >
        <div class="text-center">
          <svg
            class="animate-spin h-10 w-10 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p>Učitavanje...</p>
        </div>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6 min-h-[700px]">
        <!-- Mobilni prikaz - pokazuje ili listu ili poruke -->
        <div
          v-if="!isMobile || !activeConversation"
          class="md:col-span-1 h-[700px]"
        >
          <conversation-list @select-conversation="selectConversation" />
        </div>

        <div
          v-if="!isMobile || activeConversation"
          class="md:col-span-2 h-[700px] bg-gray-800 rounded-lg overflow-hidden"
        >
          <template v-if="activeConversation">
            <message-list
              :show-back-button="isMobile"
              @back="clearActiveConversation"
              @error="handleMessageError"
              @notify="handleNotification"
            />
          </template>
          <div
            v-else
            class="h-full flex items-center justify-center text-gray-400"
          >
            <div class="text-center p-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-16 w-16 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              <p class="text-lg">
                Izaberite konverzaciju da započnete razgovor
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast notifikacija -->
    <toast-notification
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      :title="toast.title"
      :duration="toast.duration"
      @close="closeToast"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  reactive,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import ConversationList from '@/components/chat/ConversationList.vue';
import MessageList from '@/components/chat/MessageList.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import axios from 'axios';
import { useRoute } from 'vue-router';
import { debounce } from '@/utils/debounce';

export default defineComponent({
  name: 'ChatView',
  components: {
    ConversationList,
    MessageList,
    ToastNotification,
  },
  setup() {
    const store = useStore();
    const route = useRoute();

    const activeConversation = ref(false);
    const windowWidth = ref(window.innerWidth);
    const isInitializing = ref(true);
    const showMainLoader = ref(false);
    const authError = ref(false);

    const toast = reactive({
      show: false,
      type: 'error',
      title: '',
      message: '',
      duration: 5000,
    });

    const isMobile = computed(() => windowWidth.value < 768);

    // Debounced main loader functions
    const debouncedShowMainLoader = debounce(() => {
      showMainLoader.value = true;
    }, 500);

    const hideMainLoaderImmediately = () => {
      debouncedShowMainLoader.cancel();
      showMainLoader.value = false;
    };

    // Watch main loading state
    watch(isInitializing, (loading) => {
      if (loading) {
        debouncedShowMainLoader();
      } else {
        hideMainLoaderImmediately();
      }
    });

    const selectConversation = async ({
      otherUserId,
      productId,
    }: {
      otherUserId: string;
      productId: string;
    }) => {
      try {
        // Prvo uključujemo loader
        store.commit('chat/SET_LOADING', true);

        // Postavljamo aktivnu konverzaciju odmah
        activeConversation.value = true;

        // Pokušavamo dohvatiti poruke
        try {
          await store.dispatch('chat/fetchConversationMessages', {
            otherUserId,
            productId,
          });
        } catch (error) {
          console.error('Greška pri učitavanju poruka:', error);

          // Ako je 401, znači problem s autentifikacijom
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            authError.value = true;
            localStorage.removeItem('token');
            return;
          }

          // Za ostale greške, prikazujemo toast
          handleError(error);

          // Vraćamo se na listu konverzacija
          activeConversation.value = false;
        } finally {
          store.commit('chat/SET_LOADING', false);
        }
      } catch (error) {
        console.error('Neočekivana greška:', error);
        handleError(error);
        activeConversation.value = false;
        store.commit('chat/SET_LOADING', false);
      }
    };

    const clearActiveConversation = () => {
      store.dispatch('chat/clearCurrentConversation');
      activeConversation.value = false;
    };

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    const handleMessageError = (error: any) => {
      handleError(error);
    };

    // Novi metod za rukovanje notifikacijama koje nisu greške
    const handleNotification = (notification: any) => {
      toast.show = true;
      toast.type = notification.type || 'info';
      toast.title = notification.title || 'Obaveštenje';
      toast.message = notification.message || '';
      toast.duration = notification.duration || 5000;
    };

    const handleError = (error: any) => {
      console.error('Greška:', error);
      let errorMessage = 'Došlo je do greške. Molimo pokušajte ponovo.';
      let errorTitle = 'Greška';

      if (error && typeof error === 'object') {
        if ('response' in error) {
          switch (error.response?.status) {
            case 401:
              authError.value = true;
              localStorage.removeItem('token');
              return;
            case 404:
              errorTitle = 'Oglas nije dostupan';
              errorMessage = 'Oglas više nije aktivan ili je istekao.';
              break;
            case 403:
              errorTitle = 'Pristup odbijen';
              errorMessage = 'Nemate pristup ovom razgovoru.';
              break;
            case 500:
              errorTitle = 'Greška servera';
              errorMessage =
                'Došlo je do greške na serveru. Molimo pokušajte kasnije.';
              break;
            default:
              if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
              }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      toast.show = true;
      toast.type = 'error';
      toast.title = errorTitle;
      toast.message = errorMessage;
    };

    const closeToast = () => {
      toast.show = false;
    };

    // Ključna funkcija za inicijalizaciju
    const initialize = async () => {
      isInitializing.value = true;
      authError.value = false;

      try {
        // Check for token
        const token = localStorage.getItem('token');
        if (!token) {
          authError.value = true;
          return;
        }

        // Load user data if needed
        if (!store.getters['auth/currentUser']) {
          try {
            await store.dispatch('auth/fetchCurrentUser');
          } catch (error) {
            console.error('Greška pri učitavanju korisnika:', error);
            if (axios.isAxiosError(error) && error.response?.status === 401) {
              authError.value = true;
              localStorage.removeItem('token');
              return;
            }
          }
        }

        // Load conversations with pagination (first page)
        try {
          console.log('Loading conversations with pagination...');
          await store.dispatch('chat/fetchConversations', {
            page: 1,
            limit: 4, // Match the limit from your API service
          });

          // Debug: Check what we got
          const meta = store.getters['chat/conversationsMeta'];
          const conversations = store.getters['chat/allConversations'];
          console.log('Loaded conversations:', conversations.length);
          console.log('Meta data:', meta);

          // Check if we have query parameters to open a specific conversation
          const otherUserId = route.query.otherUserId?.toString();
          const productId = route.query.productId?.toString();

          if (otherUserId && productId) {
            // Find if there's an existing conversation with this user about this product
            const existingConversation = conversations.find(
              (conv: any) =>
                conv.otherUser.id === otherUserId &&
                conv.product.id === productId
            );

            if (existingConversation) {
              // Conversation exists, select it
              selectConversation({
                otherUserId,
                productId,
              });
            } else {
              // This is a new conversation, we need to initialize it
              activeConversation.value = true;

              // Create a new conversation structure for the UI
              const productName =
                route.query.productName?.toString() || 'Proizvod';
              const productSlug = route.query.productSlug?.toString() || '';
              const otherUsername =
                route.query.otherUsername?.toString() || 'Korisnik';

              // Initialize conversation in vuex store with otherUser information
              store.commit('chat/SET_CURRENT_CONVERSATION', {
                otherUserId,
                productId,
                messages: [],
                // Add the otherUser object for the header
                otherUser: {
                  id: otherUserId,
                  username: otherUsername,
                },
                // Keep the product information
                product: {
                  id: productId,
                  name: productName,
                  slug: productSlug,
                },
              });
            }
          }
        } catch (error) {
          console.error('Greška pri učitavanju konverzacija:', error);
          handleError(error);
        }
      } catch (error) {
        console.error('Neočekivana greška pri inicijalizaciji:', error);
        handleError(error);
      } finally {
        isInitializing.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);

      // Inicijalizuj sve nakon početnog mountanja
      initialize();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      store.dispatch('chat/clearCurrentConversation');
    });

    onBeforeUnmount(() => {
      debouncedShowMainLoader.cancel();
    });

    return {
      isMobile,
      activeConversation,
      isInitializing,
      showMainLoader,
      authError,
      selectConversation,
      clearActiveConversation,
      handleMessageError,
      handleNotification,
      toast,
      closeToast,
    };
  },
});
</script>
